import { requestApi2 } from "@utils/http"
import { logError } from "@utils/logError"
import { endpoints, socketio } from "api"
import { EventSource } from "open-lib"
import { io, Socket } from "socket.io-client"
import { z } from "zod"

/**
 * Manages the "socket" to the portal server
 * Signs in (authenticate user) on connection etc
 * Has subscribable events wrt authentication status
 */
class PortalSocket {
	readonly onAuthenticated = new EventSource()
	readonly onConnected = new EventSource()
	readonly onDeAuthenticated = new EventSource()
	readonly onDisconnected = new EventSource()

	socket: Socket | undefined

	readonly #listeners = new Map<socketio.SocketIoMessageType, ((data: unknown) => void)[]>()

	#socketId: number | undefined
	#socketIsAuthenticated = false
	#userIsSignedIn = false

	constructor() {
		const socket = io()
		socket.on("connect", () => this.#onConnect(socket))
		socket.on("disconnect", () => this.#onDisconnect())
		this.addMessageListener("authResult", (message) => {
			if (message.success !== undefined) {
				this.#socketIsAuthenticated = true
				this.onAuthenticated.tryEmit()
			}
		})
	}

	get isConnected() {
		return this.socket != undefined
	}

	addEventSource<K extends socketio.SocketIoMessageType>(key: K) {
		const eventSource = new EventSource<z.infer<(typeof socketio.socketIoMessageTypes)[K]>>()
		this.addMessageListener(key, (it) => eventSource.emit(it))
		return eventSource
	}

	addMessageListener<K extends socketio.SocketIoMessageType>(
		key: K,
		fn: (data: z.infer<(typeof socketio.socketIoMessageTypes)[K]>) => void
	) {
		let array = this.#listeners.get(key)
		if (array == undefined) {
			array = []
			this.#listeners.set(key, array)
		}
		array.push((data) => {
			const parser = socketio.socketIoMessageTypes[key]
			fn(parser.parse(data))
		})
	}

	getSocketId(): number {
		if (this.#socketId == undefined) {
			throw new Error(`Socket ID is not ready`)
		}
		return this.#socketId
	}

	sendMessage<K extends socketio.SocketIoMessageType>(
		key: K,
		data: socketio.SocketIoMessageData<K>
	) {
		this.socket?.emit("message", { key, data } satisfies socketio.Message)
	}

	setUserIsSignedIn() {
		this.#userIsSignedIn = true
		this.#checkAuth().catch(logError)
	}

	async #checkAuth() {
		if (this.#socketIsAuthenticated) {
			// socket is already authenticated
			return
		}
		if (!this.isConnected || !this.#userIsSignedIn) {
			// no socket connection, or not signed in
			return
		}
		// try to authenticate socket
		const request = await requestApi2(endpoints.getSignInKey2)
		if (request == null) {
			return
		}
		this.#socketId = request.socketId
		this.sendMessage("auth", { key: request.key, socketId: this.#socketId })
	}

	#onConnect(socket: Socket) {
		this.socket = socket
		this.socket?.on("message", (m: socketio.Message) => this.#onMessage(m))
		this.socket?.on("disconnect", () => this.#onDisconnect())
		this.#checkAuth().catch(logError)
	}

	#onDisconnect() {
		this.socket = undefined
		this.onDisconnected.tryEmit()
		this.#socketIsAuthenticated = false
		this.onDeAuthenticated.tryEmit()
	}

	#onMessage(message: socketio.Message) {
		const listeners = this.#listeners.get(message.key) ?? []
		for (const listener of listeners) {
			try {
				listener(message.data as {})
			} catch (error) {
				console.error(error)
			}
		}
	}
}

export const portalSocket = new PortalSocket()

export const portalSocketEvents = {
	onWebPortalWarning: portalSocket.addEventSource("webPortalWarning"),
} as const
